/**
 * Google Analytics Integration
 *
 * Uses the gtag API directly (configured in index.html with Consent Mode v2).
 * Analytics data is only sent when the user accepts cookies via the consent banner.
 */

declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
  }
}

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value,
  });
};

/** Track user signup */
export const trackSignup = (method: 'email' | 'google' | 'microsoft' = 'email') => {
  trackEvent('User', 'Signup', method);
};

/** Track user login */
export const trackLogin = (method: 'email' | 'google' | 'microsoft' = 'email') => {
  trackEvent('User', 'Login', method);
};

/** Track partner pairing */
export const trackPartnerPairing = (role: 'creator' | 'joiner') => {
  trackEvent('Relationship', 'Partner Paired', role);
};

/** Track stage completion */
export const trackStageCompletion = (stage: string) => {
  trackEvent('Progress', 'Stage Completed', stage);
};

/** Track contract signing */
export const trackContractSigning = (signatureType: 'drawn' | 'typed') => {
  trackEvent('Contract', 'Signed', signatureType);
};

/** Track PDF export */
export const trackPDFExport = () => {
  trackEvent('Contract', 'Exported', 'PDF');
};

/** Track privacy action */
export const trackPrivacyAction = (type: 'question' | 'section') => {
  trackEvent('Privacy', 'Marked Private', type);
};

/** Track error (reports to both GA4 and Sentry) */
export const trackError = (errorType: string, errorMessage?: string) => {
  trackEvent('Error', errorType, errorMessage);
  import('@sentry/react').then((Sentry) => {
    Sentry.captureMessage(`${errorType}: ${errorMessage || 'unknown'}`, 'error');
  }).catch(() => {});
};

// ============= FREEMIUM CONVERSION FUNNEL =============

/** Track anonymous user starting onboarding */
export const trackFreemiumOnboardingStarted = () => {
  trackEvent('Freemium', 'Onboarding Started', 'anonymous');
};

/** Track anonymous user starting individual work */
export const trackFreemiumIndividualWorkStarted = () => {
  trackEvent('Freemium', 'Individual Work Started', 'anonymous');
};

/** Track anonymous progress milestone (called at 25%, 50%, 75%, 100%) */
export const trackFreemiumProgressMilestone = (percentage: number) => {
  trackEvent('Freemium', 'Progress Milestone', `${percentage}%`, percentage);
};

/** Track registration wall shown (with trigger context) */
export const trackRegistrationWallShown = (
  variant: 'save-progress' | 'invite-partner' | 'mark-complete'
) => {
  trackEvent('Freemium', 'Registration Wall Shown', variant);
};

/** Track registration wall completed (user signed up) */
export const trackRegistrationWallConverted = (
  variant: 'save-progress' | 'invite-partner' | 'mark-complete',
  responsesCount: number
) => {
  trackEvent('Freemium', 'Registration Wall Converted', variant, responsesCount);
};

/** Track save-progress nudge shown */
export const trackNudgeShown = (threshold: number) => {
  trackEvent('Freemium', 'Nudge Shown', `${threshold}%`, threshold);
};

/** Track save-progress nudge clicked (user opened registration wall from nudge) */
export const trackNudgeClicked = (threshold: number) => {
  trackEvent('Freemium', 'Nudge Clicked', `${threshold}%`, threshold);
};

/** Track join link opened */
export const trackJoinLinkOpened = (hasExistingAuth: boolean) => {
  trackEvent('Freemium', 'Join Link Opened', hasExistingAuth ? 'authenticated' : 'anonymous');
};

/** Track anonymous data migration */
export const trackMigrationComplete = (responsesCount: number, hadErrors: boolean) => {
  trackEvent('Freemium', 'Migration Complete', hadErrors ? 'with_errors' : 'clean', responsesCount);
};

/** Check if Google Analytics is available */
export const isAnalyticsEnabled = () => typeof window.gtag === 'function';

/** @deprecated GA4 is initialized in index.html. This is a no-op kept for backward compatibility. */
export const initGA = (_options?: unknown) => {};
